'use client'

import { useState, type ComponentProps } from 'react'

import { openCheckout } from '@/lib/paddle/checkout'
import BrandDisambiguationResults from './components/BrandDisambiguationResults'
import { useToolRunOrCheckout } from '@/hooks/useToolRunOrCheckout'
import ToolBrandDisambiguationRef from '@/components/reference/ToolBrandDisambiguationRef'
import type { ToolRunBinding } from '@/components/reference/tool-run'
import {
  ToolLiveError,
  ToolLiveStatus,
  ToolPayWall,
} from '@/components/reference/tool-live'
import '@/components/reference/tool-run.css'
import '@/components/reference/tool-page.css'
import '@/components/reference/tool-live.css'
import { useScrollIntoView } from '@/hooks/useScrollIntoView'

export default function BrandDisambiguationTeaserPage() {
  const scrollIntoView = useScrollIntoView()
  const [brand, setBrand] = useState('')
  const [url, setUrl] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Frozen at submit: the wall's label AND the $5 checkout both read this one
  // snapshot, never the live fields, so what the buyer is shown and what the
  // checkout pays to run cannot diverge. Cleared on any edit (handleDirty),
  // which takes the wall down with it, as on /tools/ai-citation-tracker.
  const [submitted, setSubmitted] = useState<{
    brand: string
    url: string
  } | null>(null)
  // Owner/paid → full result inline (free); everyone else → 402 → the $5 wall.
  const {
    run,
    loading,
    error: runError,
    result,
    walled,
    reset,
  } = useToolRunOrCheckout<
    ComponentProps<typeof BrandDisambiguationResults>['result']
  >('/api/tools/brand-disambiguation')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    // The check needs both the brand and the URL, so require both before the
    // buyer pays — a paid run that 400s on missing input would be a bad charge.
    if (!url.trim() || !brand.trim()) {
      setError('Enter both your brand name and website URL.')
      return
    }
    setError(null)
    setSubmitted({ brand: brand.trim(), url: url.trim() })
    void run({ url: url.trim(), brand: brand.trim() })
    scrollIntoView('disambiguation-output', 50)
  }

  // An edit after a run invalidates everything built from the old values: the
  // frozen snapshot, the wall, the result and the run error.
  function handleDirty() {
    setSubmitted(null)
    setError(null)
    reset()
  }

  function handleCheckout() {
    // No snapshot, no checkout: the wall only renders with one, so this is
    // the same guard /tools/ai-citation-tracker keeps at the spend boundary.
    if (!submitted) return
    void openCheckout('pack-single', 'tool_teaser', {
      source: 'tool_run',
      toolSlug: 'brand-disambiguation',
      url: submitted.url,
      brand: submitted.brand,
    })
  }

  const label = submitted
    ? submitted.brand || submitted.url
    : brand.trim() || url.trim() || 'your brand'

  const refRun: ToolRunBinding = {
    values: [brand, url],
    onChange: (i, v) => {
      if (i === 0) setBrand(v)
      else setUrl(v)
      handleDirty()
    },
    onSubmit: handleSubmit,
    onExample: () => {
      setBrand('Pixelmojo')
      setUrl('pixelmojo.io')
      handleDirty()
    },
    loading,
    error,
  }

  return (
    <div className='shell pg-tool pg-tool-brand-disambiguation'>
      <ToolBrandDisambiguationRef
        run={refRun}
        output={
          <>
            {(loading || runError || result || walled) && (
              <div
                id='disambiguation-output'
                className='max-w-3xl mx-auto mb-12'
              >
                {loading && (
                  <ToolLiveStatus tool='Brand Disambiguation Check'>
                    Checking disambiguation for {label}…
                  </ToolLiveStatus>
                )}

                {runError && !loading && (
                  <ToolLiveError>{runError}</ToolLiveError>
                )}

                {result && !loading && (
                  <BrandDisambiguationResults result={result} />
                )}

                {walled && submitted && !loading && (
                  <ToolPayWall
                    tool='Brand Disambiguation Check'
                    title={<>Run the disambiguation check for {label}</>}
                    onCheckout={handleCheckout}
                  >
                    Pay a one-time $5 to run the full check on{' '}
                    <strong>{label}</strong>: all 4 AI engines, the exact entity
                    each one confused you with, and the fixes to reclaim your
                    identity. Your report opens right here.
                  </ToolPayWall>
                )}
              </div>
            )}
          </>
        }
      />
    </div>
  )
}
